/**
 * Какое имя объекта видит посетитель — ОДНО правило на весь вьюер.
 *
 * Живая карточка сильнее карты: имя в карточке правит тот, кто здесь работает,
 * и правит сегодня; имя в карте рисовали один раз, и там часто остаётся вывеска
 * прежнего арендатора. Пока приоритет был обратный, переименование в кабинете
 * не меняло ничего видимого — ни подписи на карте, ни заголовка карточки
 * (Ülemiste, 01.08.2026: посетитель видел «Ristikheina Kohvik», хотя арендатор
 * уже назвался «Кафе Морого»), и это выглядело как сломанный кабинет.
 *
 * Модуль отдельный, потому что имя нужно двум слоям сразу: слою карты
 * (подписи полигонов) и UI (заголовки, поиск, каталог). Два места — два
 * расхождения, а расхождение имён на карте и в карточке хуже любого из имён.
 */
import { getLang } from '@app/i18n'
import { localized, type Venue } from '@app/rvf'
import { liveOccupants } from './state'

/** Имя из живой карточки арендатора (ADR 0014); пусто — карточки нет. */
export function liveName(venue: Venue, id: string): string {
  const live = liveOccupants.value?.get(id)
  if (!live?.name) return ''
  return localized(live.name, getLang(), venue.venue.default_language)
}

/** Имя юнита/POI для показа: живая карточка → карта. Пусто, если имени нет. */
export function displayName(venue: Venue, id: string): string {
  const live = liveName(venue, id)
  if (live) return live
  const unit = venue.units.find((u) => u.id === id)
  const poi = unit ? undefined : venue.pois.find((p) => p.id === id)
  return localized(unit?.name ?? poi?.name, getLang(), venue.venue.default_language)
}

/**
 * Категория из живой карточки — пусто, если арендатор оставил «как на карте».
 *
 * Кабинет обещает прямым текстом: «влияет на значок и подпись объекта у
 * посетителя». Пока категория читалась только из карты, обещание не
 * выполнялось: выбор в карточке не менял на карте ничего.
 */
export function liveCategory(id: string): string {
  const value = liveOccupants.value?.get(id)?.category
  return typeof value === 'string' ? value.trim() : ''
}

/** Сайт арендатора из карточки (во вьюере показывается ссылкой). */
export function liveWebsite(id: string): string {
  const value = liveOccupants.value?.get(id)?.website
  return typeof value === 'string' ? value.trim() : ''
}
